import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { AlertTriangle } from "lucide-react";

/**
 * EmailHygieneReconciliationConfirmDialog — DASH-EMAIL-013.
 *
 * Built on the base Dialog primitive (not AlertDialog) rather than the
 * app's more common AlertDialogAction pattern, deliberately: this action
 * is async and this component needs full control over disabling
 * repeated submission while a request is in flight and surfacing an
 * inline execution error without the dialog auto-closing on click
 * (AlertDialogAction's default Radix behavior).
 *
 * Every fact shown here comes from the ALREADY-LOADED detail response
 * plus the backend-authorized `action` the operator selected — nothing is
 * reconstructed or guessed. The operator never selects or edits candidate
 * state or any mutation payload; the only input this dialog collects is
 * the required operator justification (`resolution_reason`), matching the
 * real RECON-003 request contract exactly.
 */
export default function EmailHygieneReconciliationConfirmDialog({
  open,
  onOpenChange,
  detail,
  action,
  submitting,
  error,
  onConfirm,
}) {
  const [reason, setReason] = useState("");

  if (!detail || !action) return null;

  const decision = detail.missionassist_decision || {};
  const candidateOutcome = [decision.candidate_hygiene_state, decision.candidate_production_eligibility]
    .filter(Boolean)
    .join(" / ") || "Unavailable";
  const matchedCount = detail.crm_projection?.matched_contact_count ?? 0;

  const trimmedReason = reason.trim();
  const canSubmit = !submitting && trimmedReason.length > 0;

  const handleOpenChange = (next) => {
    if (submitting) return; // cannot dismiss mid-flight
    if (!next) setReason("");
    onOpenChange?.(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="email-hygiene-reconciliation-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{action.label}?</DialogTitle>
          <DialogDescription>{action.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
            <span className="text-slate-400">Recipient</span>
            <span className="text-slate-100" data-testid="email-hygiene-reconciliation-confirm-recipient">
              {detail.recipient}
            </span>
            <span className="text-slate-400">Provider</span>
            <span className="capitalize text-slate-100">{detail.provider}</span>
            <span className="text-slate-400">Environment</span>
            <span className="capitalize text-slate-100">{detail.environment}</span>
            <span className="text-slate-400">Candidate Result</span>
            <span className="text-slate-100">{candidateOutcome}</span>
            <span className="text-slate-400">CRM Contacts Affected</span>
            <span className="font-mono text-slate-100" data-testid="email-hygiene-reconciliation-confirm-matched">
              {matchedCount}
            </span>
          </div>

          <p className="pt-2 text-xs text-slate-400">
            This reuses the already-recorded provider evidence for this reconciliation — no new event or
            candidate state is captured now.
          </p>

          <div className="pt-2">
            <label htmlFor="email-hygiene-reconciliation-reason" className="mb-1 block text-xs text-slate-400">
              Reason for this action
            </label>
            <Textarea
              id="email-hygiene-reconciliation-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Legacy GHL date-format fix deployed; re-attempting projection."
              disabled={submitting}
              data-testid="email-hygiene-reconciliation-reason-input"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-300" data-testid="email-hygiene-reconciliation-confirm-error">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={submitting} onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm?.(trimmedReason)}
            data-testid="email-hygiene-reconciliation-confirm-submit"
          >
            {submitting ? "Submitting…" : action.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
